import { TaxCodeCell } from "@/components/shared/cells/tax-code";
import type { InvoiceRowData } from "@/components/shared/data-table";
import type { FormValues as TaxCodeFormValues } from "@/components/shared/form/lineItems/taxCode";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { cn } from "@/lib/utils";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import { useUpdateManyLineItems } from "@/queries/line-items/update";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";
import type { CellContext } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";

type Props = CellContext<InvoiceRowData, unknown> & {
  showConfidence?: boolean;
  readOnly?: boolean;
};

export const DatevBuCell = ({
  row,
  showConfidence = false,
  readOnly = false,
}: Props) => {
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });
  const { taxCodes } = useGetAllTaxCodes();
  const updateLineItems = useUpdateManyLineItems({
    disableOptimisticResponse: true,
  });

  const handleRefreshRow = useCallback(
    async (values: TaxCodeFormValues) => {
      await updateLineItems(
        row.original.lineItems.map((lineItem) => lineItem.lineItemId),
        {
          taxCodeId: values.taxCodeId,
        },
      );
      fetchInvoices({
        query: {
          invoiceId: {
            value: [row.original.invoice.invoiceId],
            op: "in",
          },
        },
      });
    },
    [row.original.invoice.invoiceId, fetchInvoices],
  );

  const taxCodeIds = row.original.invoice.taxCodeIds;
  const accountIds = row.original.invoice.accountIds;
  const taxCodeConfidence = row.original.invoice.taxCodeConfidence;
  const lineItemsIds = row.original.lineItems.map(
    (lineItem) => lineItem.lineItemId,
  );
  const taxCodeUnedited = taxCodeConfidence != null;

  const currentTaxCodes = useMemo(() => {
    if (!taxCodes) return [];

    return taxCodeIds.map((taxCodeId) =>
      taxCodes.find((a) => a.taxCodeId === taxCodeId),
    );
  }, [taxCodeIds, taxCodes]);

  const tooltipDescription = currentTaxCodes.map((taxCode) => ({
    id: taxCode?.datevBu ?? "",
    name: taxCode?.name ?? "",
    justification: row.original.invoice.buJustification ?? "",
  }));

  if (
    row.original.invoice.status === InvoiceStatus.NEW ||
    row.original.invoice.status === InvoiceStatus.ERROR ||
    row.original.invoice.splitPdf === true ||
    !row.original.invoice.isBooking
  ) {
    return null;
  }

  return (
    <TaxCodeCell
      lineItemIds={lineItemsIds}
      taxCodeIds={taxCodeIds}
      accountIds={accountIds}
      onChange={handleRefreshRow}
      className={cn(confColor(showConfidence ? taxCodeConfidence : undefined))}
      tooltipDescriptions={taxCodeUnedited ? tooltipDescription : undefined}
      readOnly={readOnly}
    />
  );
};
